import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Link,
  Divider,
  Paper,
  Stack,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PageMeta from "../components/common/PageMeta";
import PageContainer from "../components/common/PageContainer";
import PageHeader from "../components/common/PageHeader";
import { useApi } from "../hooks/useApi";
import { ClientBillingProfileResponse } from "../types/billing";
import { formatPaymentMethod } from "../utils/billingFormat";
import BillingOverview from "../components/common/BillingOverview";
import { BillingBalanceField, BillingField } from "../components/common/BillingCustomerPane";

interface BillingProfilePanelProps {
  billingProfile: ClientBillingProfileResponse;
  openingPortal: boolean;
  portalError: string | null;
  onOpenPortal: () => void;
}

function ClientBillingProfilePanel({ billingProfile, openingPortal, portalError, onOpenPortal }: BillingProfilePanelProps) {
  if (billingProfile.assigned === false) {
    return (
      <Alert severity="info">
        {billingProfile.manualBilling
          ? "Your account is billed outside the portal. Contact your account manager for invoices and payment details."
          : "Billing isn't active for your company yet. Please contact Chargemont support."}
      </Alert>
    );
  }

  return (
    <BillingOverview
      subscriptions={billingProfile.subscriptions}
      invoices={billingProfile.invoices}
      pane={
        <Paper variant="outlined" component="section" aria-label="Billing" sx={{ p: 2.5 }}>
          <Stack spacing={2}>
            <BillingField label="Default payment method">
              {billingProfile.defaultPaymentMethod ? (
                formatPaymentMethod(billingProfile.defaultPaymentMethod)
              ) : (
                <Alert severity="error" sx={{ mt: 0.5 }}>
                  No default payment method set. Please add one{" "}
                  <Link
                    component="button"
                    onClick={onOpenPortal}
                    disabled={openingPortal}
                    sx={{ verticalAlign: "baseline", fontWeight: 600 }}
                  >
                    here
                  </Link>{" "}
                  to avoid service interruption.
                </Alert>
              )}
            </BillingField>
            <BillingBalanceField customer={billingProfile.customer} />
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Stack spacing={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Manage your billing in Stripe</Typography>
            <Typography variant="body2" color="text.secondary">
              Update payment methods, download invoices, and view subscription details in the Stripe billing portal.
            </Typography>
            <Button
              variant="contained"
              onClick={onOpenPortal}
              disabled={openingPortal}
              startIcon={openingPortal ? <CircularProgress size={16} color="inherit" /> : <OpenInNewIcon />}
              sx={{ backgroundColor: "#465fff", "&:hover": { backgroundColor: "#3641f5" }, textTransform: "none" }}
            >
              {openingPortal ? "Opening…" : "Open Billing Portal"}
            </Button>
            {portalError && <Alert severity="error">{portalError}</Alert>}
          </Stack>
        </Paper>
      }
    />
  );
}

export default function ClientBilling() {
  const { get, post } = useApi();
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [billingProfiles, setBillingProfiles] = useState<ClientBillingProfileResponse[]>([]);
  const [openingPortalFor, setOpeningPortalFor] = useState<number | null>(null);
  const [portalError, setPortalError] = useState<string | null>(null);

  const fetchBilling = useCallback(async () => {
    setFetchError(null);
    setLoading(true);
    try {
      const response = await get<ClientBillingProfileResponse[]>(`/api/client-billing`);
      setBillingProfiles(response);
    } catch (err: any) {
      setFetchError(err.message || "Failed to load billing info");
    } finally {
      setLoading(false);
    }
  }, [get]);

  useEffect(() => {
    fetchBilling();
  }, [fetchBilling]);

  const handleOpenPortal = async (billingProfileId: number) => {
    setPortalError(null);
    setOpeningPortalFor(billingProfileId);
    try {
      const { url } = await post<{ url: string }>(
        `/api/client-billing/billing-profiles/${billingProfileId}/portal-session`,
        { returnUrl: window.location.href },
      );
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err: any) {
      setPortalError(err.message || "Failed to open billing portal");
    } finally {
      setOpeningPortalFor(null);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <PageMeta title="Chargemont | Billing" description="Chargemont | Billing" />
        <PageHeader title="Billing" />
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      </PageContainer>
    );
  }

  return (
    <div>
      <PageMeta title="Chargemont | Billing" description="Chargemont | Billing" />
      <PageContainer>
        <PageHeader title="Billing" />

        {fetchError && <Alert severity="error" sx={{ mb: 2 }}>{fetchError}</Alert>}

        {billingProfiles.map((billingProfile, idx) => (
          <Box key={billingProfile.billingProfileId}>
            {idx > 0 && <Divider sx={{ my: 5 }} />}
            {billingProfiles.length > 1 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 700 }}>{billingProfile.billingProfileName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {billingProfile.brands.map((b) => b.name).join(", ") || "No brands"}
                </Typography>
              </Box>
            )}
            <ClientBillingProfilePanel
              billingProfile={billingProfile}
              openingPortal={openingPortalFor === billingProfile.billingProfileId}
              portalError={openingPortalFor === null ? portalError : null}
              onOpenPortal={() => handleOpenPortal(billingProfile.billingProfileId)}
            />
          </Box>
        ))}
      </PageContainer>
    </div>
  );
}
